import {
  TJobFormData,
  isDayPatternInUse,
  resolveTimeZone,
} from "./CreateJobAndSchedule.model";

/**
 * The body `POST /job` takes - and, byte for byte, the body
 * `POST /job/check-conflicts` takes.
 *
 * ONE builder for both on purpose. The preflight exists so the form and the save
 * cannot disagree about what would land on the calendar; the server guarantees
 * half of that by expanding the occurrences itself from the same
 * planCreateShifts, and this guarantees the other half by sending it the same
 * body. Two separately maintained object literals would reintroduce the
 * divergence on the client side instead - a field added to the save and missed
 * here would have the preflight answering about a different job.
 *
 * `attachment2Path` is a parameter rather than something this reads off the
 * form, and that is deliberate. The save uploads each picked document and keeps
 * the returned paths; the field feeds nothing but the job's own attachment list
 * and has no bearing on which hours clash with which. Were the upload folded in
 * here, the preflight - which runs while the scheduler is typing - would POST
 * every chosen document again on every edit. The preflight passes an empty
 * array, and the upload stays in the save where it belongs.
 */
export const buildCreateJobPayload = (
  formData: TJobFormData,
  attachment2Path: unknown[]
) => ({
  leadId: formData.leadId,
  laborGoalHours: formData.leadlaborGoalHours ?? null,
  name: formData.name,
  zoneCode: formData.zoneCode,
  // Emptied while the pattern is in use: the field is hidden then, but a crew
  // picked before the first weekday chip would otherwise be sent beside the
  // pattern - a job-wide crew on every day is the very thing the pattern exists
  // to replace.
  employeeIds: isDayPatternInUse(formData) ? [] : formData.employeeIds,
  ...(formData.supervisorIds?.length && { supervisorIds: formData.supervisorIds }),
  additionalNotes: formData.additionalNotes,
  invoiceGeneration: formData.invoiceGeneration,
  jobType: formData.jobType,
  paymentsTerm: formData.paymentsTerm,
  recurrence: formData.recurrence,
  frequency: formData.frequency,
  recurEvery: formData.recurEvery,
  daysOfWeek: formData.daysOfWeek,
  dayOfMonth: formData.dayOfMonth,
  monthOfYear: formData.monthOfYear,
  startTime: formData.startTime,
  lateInAlertTime: formData.lateInAlertTime,
  endTime: formData.endTime,
  lateOutAlertTime: formData.lateOutAlertTime,
  attachment2Path,
  // The zone the pattern's wall-clock times are read in. The backend checks it
  // against IANAZone.isValidZone.
  timeZone: resolveTimeZone(formData.timeZone),
  ...(formData.recurrence && { recurrenceEndDate: formData.recurrenceEndDate }),
  // Omitted unless it is what the job is built from: unchecking Recurrence or
  // switching to Monthly leaves the pattern behind in form state, and a pattern
  // sent with either would contradict the schedule it came with - the backend's
  // own assertDayPatternFrequency refuses the combination outright.
  ...(isDayPatternInUse(formData) && { dayPattern: formData.dayPattern }),
});
